<template>
  <footer
    v-show="footerData && footerData.length"
    class="footer-seo-links"
    :class="{ desktop: $isDesktop }"
  >
    <translate tag="h3" class="heading"> More top destinations… </translate>
    <div
      v-if="footerData && footerData.length"
      class="tabs-wrapper"
      :class="{ scrolled: tabsScrolled }"
    >
      <div class="tabs" data-seo-tabs-js>
        <button
          v-for="[index, tab] of footerData.entries()"
          :key="index"
          v-observe-visibility="
            index === 0
              ? {
                  callback: tabsScrolledHandler,
                  throttle: 200,
                  throttleOptions: { leading: 'both' },
                }
              : false
          "
          :class="[isActive(index) ? 'active' : 'inactive']"
          type="button"
          @click="setActiveTab(index)"
        >
          {{ tab.title }}
        </button>
      </div>

      <img
        src="@/assets/images/down-rounded.svg"
        class="tabs-nav forward"
        width="7"
        height="13"
        alt=""
        @click="scrollForward"
      />
      <img
        src="@/assets/images/down-rounded.svg"
        class="tabs-nav back"
        width="7"
        height="13"
        alt=""
        @click="scrollBack"
      />
      <div class="tabline" />
    </div>
    <div v-if="footerData && footerData.length" class="tabs-content">
      <ul
        v-for="[index, tab] of footerData.entries()"
        :key="index"
        class="list"
        :class="[isActive(index) ? 'visible' : 'hidden']"
      >
        <li v-for="item of tab.destinations" :key="item.name">
          <pu-link :path="item.url">
            <span class="name">{{ item.name }}</span>
          </pu-link>
        </li>
      </ul>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { useLazyAsyncData, useNuxtApp } from 'nuxt/app'
import { Ref, nextTick, onMounted, ref } from 'vue'
import { getFooterSeoLinks } from './fetch'

const activeItem = ref(0)
const tabsScrolled = ref(false)
const tabs: Ref<HTMLElement | null> = ref(null)
const route = useRoute()

const { data: footerData, error } = useLazyAsyncData(
  `footerSeoLinks-${route.params.lang || 'en-gb'}`,
  () => {
    const route = useRoute()
    const { $puLangFetch } = useNuxtApp()
    return getFooterSeoLinks(
      (route.params.lang as string) || 'en-gb',
      $puLangFetch,
    )
  },
)

if (error.value) {
  console.error(error.value)
}

onMounted(() => {
  tabs.value = document.querySelector('[data-seo-tabs-js]') as HTMLElement
})

function isActive(menuItem: number) {
  return activeItem.value === menuItem
}

function setActiveTab(activeTab: number) {
  activeItem.value = activeTab
}

function tabsScrolledHandler(isVisible: boolean) {
  tabsScrolled.value = !isVisible
}

async function scrollForward() {
  if (tabs.value) {
    await nextTick()
    tabs.value.scrollLeft += 60
  }
}

async function scrollBack() {
  if (tabs.value) {
    await nextTick()
    tabs.value.scrollLeft -= 60
  }
}
</script>

<style scoped>
footer {
  border-top: 3px solid var(--colour-page-background);
}

.footer-seo-links {
  padding-top: 2em;
  opacity: 1;
}

.heading {
  margin-left: 0;
}

.tabs {
  align-items: flex-start;
  color: var(--colour-wcag-blue);
  display: flex;
  flex-direction: row;
  font-size: var(--body);
  font-weight: normal;
  justify-content: flex-start;
  overflow-x: auto;
  overflow-y: visible;
  padding: 0 1.4em;
  position: relative;
  scroll-padding: 0 1.4em;
  scroll-snap-type: x mandatory;
  width: 100vw;
  scrollbar-width: none;
}

@supports (scrollbar-width: none) {
  .tabs {
    scrollbar-width: none;
  }

  .desktop .tabs {
    scrollbar-width: thin;
  }
}

@supports selector(::-webkit-scrollbar) {
  .footer-seo-links:not(.desktop) .tabs::-webkit-scrollbar {
    display: none;
  }
}

.tabs-wrapper {
  overflow: hidden;
  width: 100vw;
  position: relative;
}

.tabs-wrapper:after,
.tabs-wrapper:before {
  background: linear-gradient(
    to left,
    rgba(255, 255, 255, 1) 10%,
    rgba(255, 255, 255, 0.9) 20%,
    rgba(255, 255, 255, 0) 100%
  );
  bottom: 0;
  content: '';
  display: block;
  height: 100%;
  left: auto;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  transition: opacity 0.5s ease-in-out;
  width: 2.4em;
  z-index: 3;
  opacity: 0;
}

.tabs-wrapper:before {
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 1) 10%,
    rgba(255, 255, 255, 0.9) 20%,
    rgba(255, 255, 255, 0) 100%
  );
  left: 0;
  right: auto;
}

.tabline {
  margin-top: -3px;
  border-bottom: 3px solid #d4dbe2;
  width: calc(100vw - 1.4em);
  margin-left: 1.4em;
}

.tabs-wrapper.scrolled .tabline {
  margin-left: 0;
  width: 100vw;
}

.tabs-nav {
  width: 1.2em;
  opacity: 0.7;
  margin: 0;
  position: absolute;
  z-index: 4;
  top: 0.65em;
  transform: rotate(-90deg) translateY(3em);
  transition: transform 0.65s ease-in-out 500ms;
}

.forward {
  right: 0;
}

.back {
  transform: rotate(90deg) translateY(3em);
  left: 0;
}

.tabs-wrapper.scrolled .tabs-nav.back {
  transform: rotate(90deg) translateY(0);
}

.tabs-wrapper.scrolled .tabs-nav.forward {
  transform: rotate(-90deg) translateY(0);
}

.tabs-wrapper.scrolled:after,
.tabs-wrapper.scrolled:before {
  opacity: 1;
}

.tabs button {
  color: var(--colour-wcag-blue);
  border-radius: 0;
  border: none;
  box-shadow: none;
  padding: 0.5em 0;
  margin: 0 1em;
  position: relative;
  white-space: nowrap;
  background: transparent;
}

.tabs button:first-of-type {
  margin-left: 0;
}

.tabs button:last-of-type {
  margin-right: 0;
}

.tabs .active {
  font-weight: bold;
}

.tabs button:active {
  transform: translateY(0);
}

.tabs .active::after {
  content: '';
  position: absolute;
  background-color: #185ba1;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  border-radius: 3px;
  z-index: 2;
}

.tabs-content {
  width: 100vw;
  padding: 1.4em;
}

.list.visible {
  display: grid;
}

.list.hidden {
  display: none;
}

.list {
  display: grid;
  grid-auto-rows: auto;
  grid-template-columns: repeat(2, calc((100vw - 2.8em) / 2));
  column-gap: 1em;
  row-gap: 1em;
}

.list a {
  font-size: var(--body);
  text-decoration: none;
}

/* desktop & tablet */
.footer-seo-links.desktop {
  padding: 2em 5em 5em;
}

.desktop .tabs-content,
.desktop .tabs-wrapper {
  width: 100%;
}

.desktop .tabs-wrapper .tabline {
  display: none;
}

.desktop .tabs {
  justify-content: space-between;
  width: 100%;
  padding-bottom: 2em;
  border-bottom: 3px solid #d4dbe2;
}

.desktop .tabs button {
  font-size: 1.4em;
}

.desktop .list {
  grid-template-columns: repeat(4, auto);
}

.desktop .list a {
  font-size: initial;
}
</style>
